"use client";

import { useCallback } from "react";
import { usePullToRefresh } from "@/hooks/use-pull-refresh";
import { useIsIosPwa } from "@/hooks/use-is-pwa-ios";
import { TamiLoadingIndicator } from "@/components/ui/tami-loading-indicator";
import { cn } from "@/lib/utils";

type PullToRefreshProps = {
  children: React.ReactNode;
  className?: string;
  /** Ícone exibido ao puxar (antes de soltar) */
  pullIcon?: React.ReactNode;
  /** Ícone exibido durante o refresh (loading) */
  loadingIcon?: React.ReactNode;
};

/** Feedback visual com Tãmi pulsando antes do reload (~1,2s após soltar). */
const MIN_REFRESH_FEEDBACK_MS = 750;

export function PullToRefresh({
  children,
  className,
  pullIcon,
  loadingIcon,
}: PullToRefreshProps) {
  const handleRefresh = useCallback(async () => {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, MIN_REFRESH_FEEDBACK_MS);
    });
    window.location.reload();
  }, []);

  const isIosPwa = useIsIosPwa();

  const { contentRef, indicatorRef, iconRef, isRefreshing } = usePullToRefresh({
    onRefresh: handleRefresh,
    enabled: isIosPwa,
  });

  return (
    <div className={cn("relative flex h-full min-h-0 w-full flex-col", className)}>
      <div
        ref={indicatorRef}
        className={cn(
          "pointer-events-none absolute left-0 right-0 top-0 z-50 flex h-16 items-center justify-center print:hidden md:hidden",
          !isIosPwa && "opacity-0",
          isRefreshing && "opacity-100",
        )}
        aria-hidden={!isRefreshing || !isIosPwa}
        aria-busy={isRefreshing && isIosPwa}
        aria-live="polite"
      >
        {isRefreshing ? (
          loadingIcon ?? <TamiLoadingIndicator withBackdrop />
        ) : (
          <span ref={iconRef} className="inline-flex items-center justify-center">
            {pullIcon ?? (
              <TamiLoadingIndicator
                animated={false}
                withBackdrop
                logoClassName="text-muted-foreground"
              />
            )}
          </span>
        )}
      </div>

      <div ref={contentRef} className="min-h-0 min-w-0 flex-1 w-full">
        {children}
      </div>
    </div>
  );
}
